// workAreas/container.js - Refactored with Custom Hooks
import React, { useState, useEffect, useRef } from 'react';
import { updateManagementWorkArea } from 'api';
import WorkAreasView from './view';

// Import custom hooks
import { useWorkAreaState } from './hooks/useWorkAreaState';
import { useGeneralInfo } from './hooks/useGeneralInfo';
import { useServices } from './hooks/useServices';
import { useGallery } from './hooks/useGallery';
import { usePricing } from './hooks/usePricing';

const WorkAreasContainer = ({
  venueInfo,
  isLoading: parentIsLoading,
  handleUpdateWorkArea,
  handleAddWorkArea,
  handleBulkActivateResources,
  resourceTypeFilter // 'HD' for Hot Desks, 'MR' for Meeting Rooms
}) => {
  // VAT percentage (used in pricing)
  const VAT_RATE = 20;

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Refs to prevent unnecessary re-initialization
  const isSavingRef = useRef(false);
  const lastProcessedVenueRef = useRef(null);

  // Get all work areas from all floors
  const allWorkAreas = venueInfo?.floors?.flatMap(floor =>
    floor.work_areas?.map(workArea => ({
      ...workArea,
      floor: floor
    })) || []
  ) || [];

  // Check if venue has any work areas
  const hasWorkAreas = venueInfo?.floors?.some(floor =>
    floor.work_areas && floor.work_areas.length > 0
  );

  // Snackbar helpers
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Initialize custom hooks
  const workAreaState = useWorkAreaState(allWorkAreas);
  const generalInfo = useGeneralInfo(allWorkAreas, showSnackbar);
  const services = useServices(allWorkAreas, showSnackbar);
  const gallery = useGallery(allWorkAreas, showSnackbar);
  const pricing = usePricing(VAT_RATE);

  // Handle creation of a new work area
  const handleCreateWorkArea = async (workAreaData) => {
    try {
      workAreaState.setIsCreating(true);
      isSavingRef.current = true;

      if (handleAddWorkArea) {
        await handleAddWorkArea(workAreaData);
      }

      showSnackbar('Work area created successfully', 'success');
      workAreaState.setShowCreateDialog(false);

    } catch (error) {
      console.error('Error creating work area:', error);
      showSnackbar(`Failed to create work area: ${error.message}`, 'error');
    } finally {
      workAreaState.setIsCreating(false);
      isSavingRef.current = false;
    }
  };

  // Initialize state from venue data
  useEffect(() => {
    if (venueInfo?.floors?.length > 0 &&
      !isSavingRef.current &&
      venueInfo !== lastProcessedVenueRef.current) {

      lastProcessedVenueRef.current = venueInfo;

      // Initialize all hooks with venue data
      generalInfo.initializeGeneralInfo(allWorkAreas);
      services.initializeServices(allWorkAreas);
      gallery.initializeGallery(allWorkAreas);
      pricing.initializePricing(allWorkAreas);
    }
  }, [venueInfo]);

  // Prepare props for the view
  const viewProps = {
    // Data
    venueInfo,
    parentIsLoading,
    resourceTypeFilter, // Pass filter to view

    // Work Area State
    expandedWorkAreas: workAreaState.expandedWorkAreas,
    showCreateDialog: workAreaState.showCreateDialog,
    
    // Pricing
    prices: pricing.prices,
    isUpdatingPrice: pricing.isUpdatingPrice,
    
    // Snackbar
    snackbar,

    // Gallery props - simplified for immediate operations
    galleryFormData: gallery.galleryFormData,
    newImages: gallery.newImages,
    isSavingGallery: gallery.isSavingGallery,

    // Services props
    servicesFormData: services.servicesFormData,
    servicesHasChanges: services.servicesHasChanges,
    isSavingServices: services.isSavingServices,

    // GeneralInfo props
    generalInfoFormData: generalInfo.generalInfoFormData,
    generalInfoHasChanges: generalInfo.generalInfoHasChanges,
    isSavingGeneralInfo: generalInfo.isSavingGeneralInfo,

    // Computed values
    allWorkAreas,
    hasWorkAreas,
    VAT_RATE,

    // Work Area State Handlers
    handleToggleWorkArea: workAreaState.handleToggleWorkArea,
    handleCloseCreateDialog: workAreaState.handleCloseCreateDialog,
    handleCreateWorkArea,
    
    // Pricing Handlers
    handlePriceChangeWithoutVAT: pricing.handlePriceChangeWithoutVAT,
    handlePriceChangeWithVAT: pricing.handlePriceChangeWithVAT,
    
    // Snackbar Handlers
    showSnackbar,
    handleCloseSnackbar,

    // GeneralInfo handlers
    handleGeneralInfoFormChange: generalInfo.handleGeneralInfoFormChange,
    handleSaveGeneralInfo: generalInfo.handleSaveGeneralInfo,
    handleCancelGeneralInfo: generalInfo.handleCancelGeneralInfo,

    // Services handlers
    handleServicesFormChange: services.handleServicesFormChange,
    handleSaveServices: services.handleSaveServices,
    handleCancelServices: services.handleCancelServices,
    handleServiceMoveUp: services.handleServiceMoveUp,
    handleServiceMoveDown: services.handleServiceMoveDown,
    handleServiceDescriptionSave: services.handleServiceDescriptionSave,
    handleAddService: services.handleAddService,
    handleRemoveService: services.handleRemoveService,

    // Gallery handlers - immediate operations
    handleSaveGallery: gallery.handleSaveGallery, // Only for uploading new images
    handleCancelGallery: gallery.handleCancelGallery, // Only for cancelling pending uploads
    handleImageUpload: gallery.handleImageUpload,
    handleImageDelete: gallery.handleImageDelete,
    handleImageMoveLeft: gallery.handleImageMoveLeft,
    handleImageMoveRight: gallery.handleImageMoveRight,

    // Bulk activation handler
    handleBulkActivateResources,

    // External dependencies
    updateManagementWorkArea
  };

  return <WorkAreasView {...viewProps} />;
};

export default WorkAreasContainer;